import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ALL_BLOGS } from "./data";
import Navbar from "../LandingPageNew/Navbar";
import Footer from "../LandingPageNew/Footer";

const PRIMARY = "#1A4564";

/** slugify for heading ids */
function slugify(str = "") {
  return str
    .toLowerCase()
    .trim()
    .replace(/<\/?[^>]+(>|$)/g, "") // remove html if any
    .replace(/[^\w\s-]/g, "") // remove non-word
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

/** Smooth scroll with offset (navbar) */
function scrollToId(id, offset = 90) {
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top: y, behavior: "smooth" });
}

/** Build share urls */
function getShareUrls(url, title) {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title);

  return {
    copy: url,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    twitter: `https://twitter.com/intent/tweet?url=${u}&text=${t}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
    whatsapp: `https://wa.me/?text=${t}%20${u}`,
  };
}

/** Simple icon buttons (no libs) */
const IconLink = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M10 13a5 5 0 0 1 0-7l1-1a5 5 0 0 1 7 7l-1 1"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M14 11a5 5 0 0 1 0 7l-1 1a5 5 0 0 1-7-7l1-1"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const IconShare = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M16 8a3 3 0 1 0-2.83-4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M6 14a3 3 0 1 0 2.83 4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M8.8 15.4 15.2 11.6M15.2 8.4 8.8 12.2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const SocialBtn = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:shadow"
  >
    {children}
  </a>
);

export default function BlogDetail() {
  const { slug } = useParams();
  const articleRef = useRef(null);

  const post = useMemo(() => ALL_BLOGS.find((p) => p.slug === slug), [slug]);

  // current page url (client-side)
  const [pageUrl, setPageUrl] = useState("");
  useEffect(() => {
    if (typeof window !== "undefined") setPageUrl(window.location.href);
  }, [slug]);

  // TOC state
  const [toc, setToc] = useState([]); // [{id, text, level}]
  const [copied, setCopied] = useState(false);
  // ✅ Active heading in TOC
  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    if (!toc.length) return;

    const elements = toc
      .map((x) => document.getElementById(x.id))
      .filter(Boolean);

    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // pick the visible entry closest to the top
        const visible = entries.filter((e) => e.isIntersecting);
        if (!visible.length) return;

        // smallest top = closest to top of viewport
        visible.sort(
          (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
        );
        setActiveId(visible[0].target.id);
      },
      {
        // important: offset for navbar so the "active" switches nicely
        root: null,
        rootMargin: "-120px 0px -65% 0px",
        threshold: [0.1, 0.2, 0.3],
      },
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [toc]);

  // Build TOC after content renders
  useEffect(() => {
    if (!post) return;
    const root = articleRef.current;
    if (!root) return;

    // find headings inside article
    const headings = Array.from(root.querySelectorAll("h2, h3"));

    const used = new Set();
    const items = headings
      .map((h) => {
        const text = (h.textContent || "").trim();
        if (!text) return null;

        // ensure unique id
        let id = h.getAttribute("id");
        if (!id) id = slugify(text);

        let finalId = id;
        let i = 2;
        while (used.has(finalId) || document.getElementById(finalId)) {
          finalId = `${id}-${i++}`;
        }
        used.add(finalId);
        h.setAttribute("id", finalId);

        return {
          id: finalId,
          text,
          level: h.tagName.toLowerCase() === "h2" ? 2 : 3,
        };
      })
      .filter(Boolean);

    setToc(items);
  }, [post]);

  const relatedPosts = useMemo(() => {
    if (!post) return [];

    const sameCategory = post.category
      ? ALL_BLOGS.filter(
          (p) => p.slug !== post.slug && p.category === post.category,
        )
      : [];

    const postTags = Array.isArray(post.tags) ? post.tags : [];
    const byTags =
      postTags.length > 0
        ? ALL_BLOGS.filter((p) => {
            if (p.slug === post.slug) return false;
            const t = Array.isArray(p.tags) ? p.tags : [];
            return t.some((x) => postTags.includes(x));
          })
        : [];

    // Merge unique, then take 4
    const map = new Map();
    [...sameCategory, ...byTags].forEach((p) => map.set(p.slug, p));
    return Array.from(map.values()).slice(0, 4);
  }, [post]);

  const sidebarRecent = useMemo(() => {
    // If you have a real date sort, sort here. Otherwise, just show first N excluding current.
    return ALL_BLOGS.filter((p) => p.slug !== slug).slice(0, 5);
  }, [slug]);

  const categories = useMemo(() => {
    const cats = new Map();
    ALL_BLOGS.forEach((p) => {
      if (!p.category) return;
      cats.set(p.category, (cats.get(p.category) || 0) + 1);
    });
    return Array.from(cats.entries()).map(([name, count]) => ({ name, count }));
  }, []);

  const tags = useMemo(() => {
    const m = new Map();
    ALL_BLOGS.forEach((p) => {
      const t = Array.isArray(p.tags) ? p.tags : [];
      t.forEach((x) => m.set(x, (m.get(x) || 0) + 1));
    });
    return Array.from(m.entries())
      .map(([name, count]) => ({ name, count }))
      .slice(0, 16);
  }, []);

  if (!post) {
    return (
      <section className="bg-white">
        <div className="mx-auto w-[90%] 2xl:w-[70%] py-20">
          <h1 className="text-2xl font-extrabold" style={{ color: PRIMARY }}>
            Blog not found
          </h1>
          <p className="mt-3 text-slate-600">
            The article you’re looking for doesn’t exist.
          </p>
          <Link
            to="/ratgeber"
            className="mt-6 inline-flex rounded-xl border border-slate-200 px-4 py-2 font-semibold text-slate-700"
          >
            Zurück zu Blogs
          </Link>
        </div>
      </section>
    );
  }

  const share = getShareUrls(pageUrl || "", post.title);

  return (
    <>
      <Navbar />

      <section className="bg-white pt-16">
        <div className="mx-auto w-[90%] 2xl:w-[70%] py-12">
          <Link
            to="/ratgeber"
            className="text-sm font-semibold text-slate-600 hover:text-slate-900"
          >
            ← Zurück zu Blogs
          </Link>

          <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_360px]">
            {/* ===================== MAIN ===================== */}
            <main>
              {/* header */}
              <h1
                className="text-3xl 2xl:text-5xl font-extrabold tracking-tight"
                style={{ color: PRIMARY }}
              >
                {post.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <img
                    src={post.authorAvatar}
                    alt={post.author}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                  <span className="font-semibold text-slate-700">
                    {post.author}
                  </span>
                </div>
                <span>•</span>
                <span className="font-medium">{post.date}</span>

                {post.category ? (
                  <>
                    <span>•</span>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                      {post.category}
                    </span>
                  </>
                ) : null}

                {post.readingTime ? (
                  <>
                    <span>•</span>
                    <span className="font-medium">{post.readingTime}</span>
                  </>
                ) : null}
              </div>

              {/* hero image */}
              <div className="mt-8 overflow-hidden rounded-3xl border border-slate-200">
                <img
                  src={post.image}
                  alt={post.title}
                  className="h-[260px] w-full object-cover sm:h-[380px]"
                />
              </div>

              {/* share + outline quick actions (top) */}
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <IconShare className="h-5 w-5 text-slate-700" />
                  Aktie:
                  <div className="ml-2 flex items-center gap-2">
                    <SocialBtn
                      href={share.facebook}
                      aria-label="Share on Facebook"
                    >
                      f
                    </SocialBtn>
                    <SocialBtn href={share.twitter} aria-label="Share on X">
                      x
                    </SocialBtn>
                    <SocialBtn
                      href={share.linkedin}
                      aria-label="Share on LinkedIn"
                    >
                      in
                    </SocialBtn>
                    <SocialBtn
                      href={share.whatsapp}
                      aria-label="Share on WhatsApp"
                    >
                      wa
                    </SocialBtn>

                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(share.copy);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 1400);
                        } catch {
                          // ignore
                        }
                      }}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:shadow"
                    >
                      <IconLink className="h-4 w-4" />
                      {copied ? "Copied!" : "Link kopieren"}
                    </button>
                  </div>
                </div>
              </div>

              {/* content */}
              <article
                ref={articleRef}
                className="prose prose-slate max-w-none mt-10"
              >
                <div dangerouslySetInnerHTML={{ __html: post.content }} />
              </article>

              {/* bottom share row */}
              <div className="mt-10 flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white p-6">
                <div className="text-sm font-extrabold text-slate-900">
                  Teilen Sie diesen Artikel
                </div>
                <div className="flex items-center gap-2">
                  <SocialBtn href={share.facebook}>f</SocialBtn>
                  <SocialBtn href={share.twitter}>x</SocialBtn>
                  <SocialBtn href={share.linkedin}>in</SocialBtn>
                  <SocialBtn href={share.whatsapp}>wa</SocialBtn>
                </div>
              </div>

              {/* related posts */}
              {relatedPosts.length > 0 ? (
                <div className="mt-12">
                  <div className="flex items-end justify-between">
                    <h3
                      className="text-xl font-extrabold"
                      style={{ color: PRIMARY }}
                    >
                      Verwandte Beiträge
                    </h3>
                    <Link
                      to="/ratgeber"
                      className="text-sm font-semibold text-slate-600 hover:text-slate-900"
                    >
                      View all
                    </Link>
                  </div>

                  <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {relatedPosts.map((p) => (
                      <Link
                        key={p.slug}
                        to={`/ratgeber/${p.slug}`}
                        className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow"
                      >
                        <div className="h-44 overflow-hidden">
                          <img
                            src={p.image}
                            alt={p.title}
                            className="h-full w-full object-cover transition group-hover:scale-[1.03]"
                          />
                        </div>
                        <div className="p-5">
                          <div className="text-xs font-bold text-slate-500">
                            {p.category || p.tag || "Blog"}
                          </div>
                          <div className="mt-2 text-base font-extrabold text-slate-900 line-clamp-2">
                            {p.title}
                          </div>
                          {p.excerpt ? (
                            <p className="mt-2 text-sm text-slate-600 line-clamp-2">
                              {p.excerpt}
                            </p>
                          ) : null}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : null}
            </main>

            {/* ===================== SIDEBAR ===================== */}
            <aside className="hidden lg:block">
              {/* ✅ Sticky Outline */}
             {toc.length > 0 ? (
  <div className="fixed right-[5%] 2xl:right-[15%] top-24 w-[360px]">
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
      {/* header fixed */}
      <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
        <h3 className="text-base font-extrabold text-slate-900">Gliederung</h3>
        
      </div>

      {/* scrollable list */}
      <div className="max-h-[100vh] overflow-auto px-3 py-3">
        <ul className="space-y-1.5">
          {toc.map((item) => {
            const isActive = item.id === activeId;

            return (
              <li key={item.id} className={item.level === 3 ? "pl-4" : ""}>
                <button
                  type="button"
                  onClick={() => scrollToId(item.id)}
                  className={[
                    "group w-full rounded-xl px-3 py-2 text-left text-sm font-semibold transition",
                    isActive
                      ? "bg-slate-900 text-white"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                  ].join(" ")}
                >
                  <span className="flex items-start gap-2">
                    <span
                      className={[
                        "mt-1.5 h-4 w-1 rounded-full transition",
                        isActive ? "bg-white" : "bg-transparent group-hover:bg-slate-300",
                      ].join(" ")}
                    />
                    <span className="flex-1 leading-snug">{item.text}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  </div>
) : null}


              {/* Soziale Netzwerke */}
              <div className="rounded-3xl border border-slate-200 mt-36 mb-2 bg-white p-6">
                <h3 className="text-base font-extrabold text-slate-900">
                  Soziale Netzwerke
                </h3>
                <div className="mt-4 grid grid-cols-4 gap-3">
                  {/* Replace these links with your real socials */}
                  <SocialBtn
                    href="https://instagram.com"
                    aria-label="Instagram"
                  >
                    ig
                  </SocialBtn>
                  <SocialBtn href="https://facebook.com" aria-label="Facebook">
                    f
                  </SocialBtn>
                  <SocialBtn href="https://twitter.com" aria-label="X">
                    x
                  </SocialBtn>
                  <SocialBtn href="https://linkedin.com" aria-label="LinkedIn">
                    in
                  </SocialBtn>
                </div>
              </div>

              {/* Categories */}
              {categories.length > 0 ? (
                <div className="rounded-3xl border border-slate-200 mb-2 bg-white p-6">
                  <h3 className="text-base font-extrabold text-slate-900">
                    Kategorie
                  </h3>
                  <div className="mt-4 space-y-3">
                    {categories.slice(0, 6).map((c) => (
                      <button
                        key={c.name}
                        type="button"
                        className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-100"
                      >
                        <span>{c.name}</span>
                        <span className="rounded-full bg-white px-2 py-0.5 text-xs font-extrabold text-slate-600">
                          {c.count}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Mini posts list */}
              <div className="rounded-3xl border mb-2 border-slate-200 bg-white p-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-extrabold text-slate-900">
                    Aktuelle Beiträge
                  </h3>
                  <Link
                    to="/ratgeber"
                    className="text-sm font-semibold text-slate-600 hover:text-slate-900"
                  >
                    Alle anzeigen
                  </Link>
                </div>

                <div className="mt-4 space-y-4">
                  {sidebarRecent.map((p) => (
                    <Link
                      key={p.slug}
                      to={`/ratgeber/${p.slug}`}
                      className="group flex gap-3"
                    >
                      <div className="h-16 w-16 overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
                        <img
                          src={p.image}
                          alt={p.title}
                          className="h-full w-full object-cover transition group-hover:scale-[1.03]"
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-slate-500">
                          {p.category || p.tag || "Blog"}
                        </div>
                        <div className="mt-1 text-sm font-extrabold text-slate-900 line-clamp-2">
                          {p.title}
                        </div>
                        <div className="mt-1 text-xs font-semibold text-slate-500">
                          {p.date}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Tag cloud */}
              {tags.length > 0 ? (
                <div className="rounded-3xl mb-2 border border-slate-200 bg-white p-6">
                  <h3 className="text-base font-extrabold text-slate-900">
                    Schlagwortwolke
                  </h3>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {tags.map((t) => (
                      <button
                        key={t.name}
                        type="button"
                        className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-700 hover:bg-slate-50"
                        title={`${t.count} posts`}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Optional: a small CTA card */}
              <div className="rounded-3xl border border-slate-200 bg-slate-900 p-6 text-white">
                <div className="text-sm font-extrabold">
                  Newsletter abonnieren
                </div>
                <p className="mt-2 text-sm text-white/80">
                  Erhalten Sie die neuesten Beiträge direkt in Ihren
                  Posteingang.
                </p>
                <div className="mt-4 flex gap-2">
                  <input
                    className="h-11 w-full rounded-xl bg-white/10 px-3 text-sm outline-none ring-1 ring-white/15 placeholder:text-white/60"
                    placeholder="Your email"
                  />
                  <button
                    className="h-11 rounded-xl bg-white px-4 text-sm font-extrabold text-slate-900"
                    style={{ boxShadow: "0 10px 30px rgba(0,0,0,.25)" }}
                  >
                    Verbinden
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
